import { queryOptions } from '@tanstack/react-query';

import { queryClient } from '@/services/query-client';

import { fetchSupplierDiversities } from '../data-provider';
import type { SupplierDiversities, SupplierDiversity, SupplierDiversityFilters } from '../models';
import { CACHE_KEYS } from '../types';
import { logger } from '../utils/logger';

/**
 * Internal supplier diversity lookup cache used to resolve supplier diversity references
 * without repeatedly requesting the same supplier diversity data.
 */

export const supplierDiversitiesQueryOptions = (
  filters: Partial<SupplierDiversityFilters> = {},
  { enabled = true }: { enabled?: boolean } = {},
) =>
  queryOptions({
    enabled,
    queryFn: async ({ signal }) => {
      const supplierDiversities = await fetchSupplierDiversities(filters, { signal });
      put(supplierDiversities.list);
      return supplierDiversities;
    },
    queryKey: CACHE_KEYS.supplierDiversities(filters),
    // biome-ignore lint/style/noMagicNumbers: 5 minutes
    staleTime: 1000 * 60 * 5,
  });

const INTERNAL_SUPPLIER_DIVERSITIES_CACHE_KEY = btoa('internal-supplier-diversities-cache-key');

const INTERNAL_QUERY_SUPPLIER_DIVERSITIES_KEY = supplierDiversitiesQueryOptions({
  codeDesc: INTERNAL_SUPPLIER_DIVERSITIES_CACHE_KEY,
}).queryKey;

const getSupplierDiversityFallback = (supplierDiversityId: string): SupplierDiversity => {
  logger.warn(`Supplier diversity "${supplierDiversityId}" was not found. Verify the data model.`);
  return {
    codeDesc: supplierDiversityId,
    id: supplierDiversityId,
    label: supplierDiversityId,
    lookupCodeType: 'SPLDVRST',
  };
};

export const put = async (supplierDiversities: SupplierDiversity[]) => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_SUPPLIER_DIVERSITIES_KEY);

  const newSupplierDiversities = supplierDiversities.reduce(
    (acc, supplierDiversity) => {
      acc[String(supplierDiversity.id)] = supplierDiversity;
      return acc;
    },
    { ...(data?.map ?? {}) },
  );

  queryClient.setQueryData(INTERNAL_QUERY_SUPPLIER_DIVERSITIES_KEY, {
    list: Object.values(newSupplierDiversities),
    map: newSupplierDiversities,
  });
};

export const getAll = (): SupplierDiversities | undefined => {
  return queryClient.getQueryData(INTERNAL_QUERY_SUPPLIER_DIVERSITIES_KEY);
};

export const find = async (supplierDiversityId: string): Promise<SupplierDiversity> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_SUPPLIER_DIVERSITIES_KEY);

  const supplierDiversity = data?.map[supplierDiversityId];
  if (supplierDiversity) {
    return supplierDiversity;
  }

  try {
    const newData = await queryClient.query(
      supplierDiversitiesQueryOptions({ search: { codeId: supplierDiversityId } }),
    );
    if (newData.map[supplierDiversityId]) {
      void put(newData.list);
      return newData.map[supplierDiversityId];
    }
  } catch {
    return getSupplierDiversityFallback(supplierDiversityId);
  }

  return getSupplierDiversityFallback(supplierDiversityId);
};

export const findAll = async (supplierDiversityIds: string[]): Promise<SupplierDiversity[]> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_SUPPLIER_DIVERSITIES_KEY);

  const missingIds: string[] = [];
  const supplierDiversities: SupplierDiversity[] = [];

  for (const supplierDiversityId of supplierDiversityIds) {
    const item = data?.map[supplierDiversityId];
    if (item) {
      supplierDiversities.push(item);
    } else {
      missingIds.push(supplierDiversityId);
    }
  }

  if (!missingIds.length) {
    return supplierDiversities;
  }

  try {
    const newData = await queryClient.query(
      supplierDiversitiesQueryOptions({ search: { codeId: missingIds.join(',') } }),
    );
    void put(newData.list);
    for (const missingId of missingIds) {
      const item = newData.map[missingId];
      if (item) {
        supplierDiversities.push(item);
      } else {
        supplierDiversities.push(getSupplierDiversityFallback(missingId));
      }
    }
  } catch {
    for (const missingId of missingIds) {
      supplierDiversities.push(getSupplierDiversityFallback(missingId));
    }
  }

  return supplierDiversities;
};
